import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";

import { Box, Button } from "@chakra-ui/react";

import getAirPayoutStatusBadge from "components/Journey/AirPayout/getAirPayoutStatusBadge";
import { PAGE_PATHS } from "config/routes";
import { Journey } from "models/Journey";
import { AirPayoutStatus } from "models/journeys/AirPayoutJourney";
import { TranslationKey } from "types/i18n";

import ProtectBoxContent from "./ProtectBoxContent";

interface IComponent {
  journey: Journey;
}

const AirPayoutProtectBox: React.FC<IComponent> = ({ journey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, mainAirPayoutJourney } = journey;
  const status = mainAirPayoutJourney.status;
  const { outboundOnly, cancelationOnly } = mainAirPayoutJourney;

  const airPayoutPayout = `${mainAirPayoutJourney.payoutAmount.toString()}`;

  const getPaidHandler = (update: boolean) => {
    const page = update ? PAGE_PATHS.JOURNEY.AIR_PAYOUT.PAYOUT_UPDATE_PAGE : PAGE_PATHS.JOURNEY.AIR_PAYOUT.PAYOUT_PAGE;
    navigate(generatePath(page, { id: id.toString() }));
  };

  const getAdditionalContent = (tKey: TranslationKey, update = false) => {
    const id = update ? "air-payout-update-bank-details-button" : "air-payout-get-paid-button";
    return (
      <Box mt={0} py={4} px={4} border="0" borderTop="1px solid" borderTopColor="greyscale.400">
        <Button onClick={() => getPaidHandler(update)} size="s" width="100%" data-testid={id}>
          {t(tKey)}
        </Button>
      </Box>
    );
  };

  const getActiveStatusDescription = () => {
    if (outboundOnly && cancelationOnly) {
      return t("air_payout.card.description.cancelation");
    }
    return t("air_payout.card.description.added");
  };

  const getContent = (status?: AirPayoutStatus) => {
    switch (status) {
      case AirPayoutStatus.ACTIVE:
        return {
          description: getActiveStatusDescription(),
        };
      case AirPayoutStatus.ELIGIBLE:
        return {
          description: t("air_payout.card.description.eligible", { amount: airPayoutPayout }),
          additionalContent: getAdditionalContent("common.get_paid"),
        };
      case AirPayoutStatus.INELIGIBLE:
      case AirPayoutStatus.UNREGISTERED:
        return {
          description: t("air_payout.card.description.not_eligible"),
        };
      case AirPayoutStatus.PROCESSING_PAYMENT:
      case AirPayoutStatus.MANUAL_VERIFICATION_PENDING:
        return {
          description: t("air_payout.card.description.processing"),
        };

      case AirPayoutStatus.PAYOUT_FAILED:
      case AirPayoutStatus.PAYOUT_RETURNED:
        return {
          description: t("air_payout.card.description.payment_failed"),
          additionalContent: getAdditionalContent("common.update_bank_details", true),
        };
      case AirPayoutStatus.PAYOUT_SUCCESSFUL:
        return {
          description: t("air_payout.card.description.paid"),
        };
      default:
        return {
          description: t("air_payout.card.description.default", { amount: airPayoutPayout }),
        };
    }
  };

  const boxTag = getAirPayoutStatusBadge(status);
  const boxContent = getContent(status);

  return <ProtectBoxContent boxTag={boxTag} boxContent={boxContent} mainAirPayoutJourney={mainAirPayoutJourney} />;
};

export default AirPayoutProtectBox;
